/**
 * MoveCommentModal - move one message to another existing ticket.
 *
 * For the reply that lands on the right thread by the rules but is about
 * the other topic (after IP2 / RM8 were split, Andrea answering the Cord
 * Daddy email with SpineRX news). The message keeps its id, time, files
 * and history; moving an email also moves where replies to it go.
 *
 * For a customer email the dialog offers to bring the later replies to
 * that email along (the split suggestion's reply chain). Notes move
 * alone. Ticket lookup is the agent hybrid search, like the merge modal.
 */
import { useEffect, useMemo, useState } from "react";
import { isAxiosError } from "axios";

import { Alert, Button, Modal, Spinner } from "@/components/ui";
import { StatusBadge } from "@/components/tickets/StatusBadge";
import { useAgentTicketSearch } from "@/hooks/useAgentTicketSearch";
import { useMoveComments, useSplitSuggestion } from "@/hooks/useSplitTicket";
import type {
  CommentResponse,
  SearchResultResponse,
  TicketResponse,
  TicketStatus,
} from "@/types/api";

export interface MoveCommentModalProps {
  /** The message to move; null while closed. */
  comment: CommentResponse | null;
  onClose: () => void;
  ticket: TicketResponse;
  /** Called with the destination's public code and how many moved. */
  onMoved: (targetPublicCode: string, count: number) => void;
}

function errorDetail(err: unknown): string {
  if (isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string" && detail) return detail;
  }
  return "The move may not have completed - refresh and check both tickets before retrying.";
}

export function MoveCommentModal({
  comment,
  onClose,
  ticket,
  onMoved,
}: MoveCommentModalProps) {
  const isOpen = comment !== null;
  const isEmail =
    !!comment &&
    comment.author_kind === "customer" &&
    !comment.is_private &&
    !comment.is_draft;
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [target, setTarget] = useState<SearchResultResponse | null>(null);
  const [withReplies, setWithReplies] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const moveComments = useMoveComments();
  const chain = useSplitSuggestion(
    ticket.ticket_number,
    isOpen && isEmail ? comment.id : null,
  );

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setDebouncedQuery("");
      setTarget(null);
      setWithReplies(true);
      setError(null);
    }
  }, [isOpen, comment?.id]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  const search = useAgentTicketSearch(isOpen ? debouncedQuery : "", {}, 10);
  const results = useMemo(
    () => (search.data?.pages ?? []).flatMap((p) => p.data),
    [search.data],
  );

  // Later emails that reply to this one (the chain minus itself).
  const replyIds = (chain.data?.comment_ids ?? []).filter(
    (id) => id !== comment?.id,
  );
  const ids = comment
    ? [comment.id, ...(isEmail && withReplies ? replyIds : [])]
    : [];

  function safeClose() {
    if (!moveComments.isPending) onClose();
  }

  async function confirm() {
    if (!target || !comment) return;
    setError(null);
    try {
      const result = await moveComments.mutateAsync({
        ticketNumber: ticket.ticket_number,
        commentIds: ids,
        targetTicketNumber: target.ticket_number,
      });
      onMoved(result.target.public_code, result.moved_comment_ids.length);
    } catch (err) {
      setError(errorDetail(err));
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={safeClose} title="Move to another ticket" size="lg">
      <p className="text-sm text-neutral-700">
        {isEmail
          ? "The email moves with its files and history, and replies to it go to the ticket you pick. Nobody is emailed."
          : "The message moves with its files and history. Nobody is emailed."}
      </p>

      {isEmail && (
        <label className="mt-3 flex items-start gap-2 text-sm text-neutral-800">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={withReplies}
            onChange={(e) => setWithReplies(e.target.checked)}
            disabled={replyIds.length === 0}
            data-testid="move-with-replies"
          />
          <span>
            {chain.isLoading
              ? "Looking for later replies to this email..."
              : replyIds.length === 0
                ? "No later replies to this email on this ticket."
                : `Also move ${replyIds.length} later ${
                    replyIds.length === 1 ? "reply" : "replies"
                  } to this email`}
          </span>
        </label>
      )}

      <div className="mt-4">
        <label
          htmlFor="move-search"
          className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500"
        >
          Move to
        </label>
        <input
          id="move-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by code, subject, or requester"
          autoComplete="off"
          data-testid="move-search"
          className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>

      <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-neutral-200">
        {debouncedQuery.trim().length === 0 ? (
          <p className="p-3 text-sm text-neutral-500">Type to search tickets.</p>
        ) : search.isLoading ? (
          <div className="flex items-center gap-2 p-3 text-sm text-neutral-500">
            <Spinner size="sm" /> Searching...
          </div>
        ) : results.length === 0 ? (
          <p className="p-3 text-sm text-neutral-500">No tickets matched.</p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {results.map((row) => {
              const disabled =
                row.ticket_number === ticket.ticket_number ||
                /^g-/i.test(row.public_code);
              const isPicked = target?.ticket_id === row.ticket_id;
              return (
                <li key={row.ticket_id}>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setTarget(isPicked ? null : row)}
                    aria-pressed={isPicked}
                    data-testid={`move-target-${row.public_code}`}
                    className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm ${
                      disabled
                        ? "cursor-not-allowed opacity-45"
                        : isPicked
                          ? "bg-primary-50"
                          : "hover:bg-neutral-50"
                    }`}
                  >
                    <i
                      className={`fa-duotone fa-thin ${
                        isPicked ? "fa-circle-dot" : "fa-circle"
                      } text-neutral-500`}
                      aria-hidden="true"
                    />
                    <span className="w-20 shrink-0 font-mono text-xs text-neutral-600">
                      [#{row.public_code}]
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-neutral-900">
                        {row.subject}
                      </span>
                      <span className="block truncate text-xs text-neutral-500">
                        {row.requester_name || row.requester_email}
                      </span>
                    </span>
                    <StatusBadge status={row.status as TicketStatus} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {error && (
        <Alert variant="error" className="mt-3">
          {error}
        </Alert>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={safeClose}
          disabled={moveComments.isPending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={() => {
            void confirm();
          }}
          disabled={!target || moveComments.isPending}
          isLoading={moveComments.isPending}
          data-testid="move-confirm"
        >
          {target
            ? `Move ${ids.length} message${ids.length === 1 ? "" : "s"} to [#${target.public_code}]`
            : "Move"}
        </Button>
      </div>
    </Modal>
  );
}

export default MoveCommentModal;
